import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Box, Stack } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import DateRangePicker from 'components/DateRangePicker/DateRangePicker'
import ExportButton from 'components/ExportButton'
import Header from 'components/Header'
import StudentSearchBar from 'components/StudentSearch'
import { useSession } from 'components/Providers'
import { getNurseComplaintTypes, getNurseDispositionTypes, getNurseTreatmentTypes } from 'store/lookup'
import { searchVisit, setVisitQuery } from 'store/nurse/nurse-report'
import VisitReportList from './List'
import useVisitReportData from './useVisitReportData'

const FORMAT = 'YYYY-MM-DD'

const thisMonth = () => [dayjs().startOf('month'), dayjs().endOf('month')].map(date => date.format(FORMAT))

export default function VisitReportPage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { district, academicYear } = useSession()
  const { rows, columns, loading } = useVisitReportData()
  const allStudents = useSelector(state => state.lookup.allStudents.rows)
  const persistedQuery = useSelector(state => state.nurse.report.visitQuery)

  const [searchParams, setSearchParams] = useSearchParams()
  const initial = useRef(null)
  if (!initial.current) {
    const [start, end] = thisMonth()
    initial.current = {
      startDate: persistedQuery?.startDate || start,
      endDate: persistedQuery?.endDate || end,
      studentId: persistedQuery?.studentId ?? null,
    }
  }

  const seeded = Boolean(searchParams.get('startDate') && searchParams.get('endDate'))
  const startDate = seeded ? searchParams.get('startDate') : initial.current.startDate
  const endDate = seeded ? searchParams.get('endDate') : initial.current.endDate
  const studentId = seeded ? Number(searchParams.get('studentId')) || null : initial.current.studentId

  const student = useMemo(
    () => (studentId ? allStudents?.find(s => s.personId === studentId) ?? null : null),
    [allStudents, studentId],
  )

  const updateParams = useRef()
  updateParams.current = (patch, options) => {
    const params = new URLSearchParams(searchParams)
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') params.delete(key)
      else params.set(key, value)
    })
    setSearchParams(params, { replace: true, ...options })
  }

  useEffect(() => {
    dispatch(getNurseComplaintTypes())
    dispatch(getNurseTreatmentTypes())
    dispatch(getNurseDispositionTypes())
  }, [dispatch])

  useEffect(() => {
    if (!seeded) updateParams.current({ startDate, endDate, studentId })
  }, [seeded, startDate, endDate, studentId])

  useEffect(() => {
    const query = { startDate, endDate, ...(studentId ? { studentId } : {}) }
    dispatch(setVisitQuery(query))
    dispatch(searchVisit(query))
  }, [dispatch, startDate, endDate, studentId, district?.id, academicYear?.id])

  const onDateRange = useCallback(range => {
    updateParams.current({ startDate: range[0].format(FORMAT), endDate: range[1].format(FORMAT) })
  }, [])

  const onStudent = nextStudent => updateParams.current({ studentId: nextStudent?.personId ?? null })

  return (
    <Box>
      <Header title={t('Visit Report')} small sx={{ paddingTop: 2 }} />
      <Stack direction="row" alignItems="center" spacing={2} sx={{ my: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <DateRangePicker
            label={t('Date Range')}
            startDate={startDate}
            endDate={endDate}
            setDateRange={onDateRange}
            showQuickSelect
            allowWeekends
          />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <StudentSearchBar value={student} onSelect={onStudent} />
        </Box>
        <ExportButton
          columns={columns}
          rows={rows}
          fileName="visit-report"
          sheetName={t('Visit Report')}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        />
      </Stack>
      <VisitReportList rows={rows} columns={columns} loading={loading} />
    </Box>
  )
}
